import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CalendarGrid from './CalendarGrid';
import { ROOMS } from '../Constants/rooms';
import { fetchAvailability } from '../Util/reservationApi';
import { formatKoreanDate, shiftMonth, todayYmd } from '../Util/dateUtil';
import '../CSS/style/Availability.css';

const REFRESH_MS = 60 * 1000;   // 1분마다 자동 갱신
const MAX_MONTHS_AHEAD = 12;    // 앞으로 볼 수 있는 개월 수

const now = new Date();
const THIS_MONTH = { year: now.getFullYear(), month: now.getMonth() + 1 };

/**
 * @param {'page'|'modal'} variant
 *   page  — 메인 페이지에 그대로 놓이는 형태 (섹션 제목 포함)
 *   modal — 팝업 안에 들어가는 형태 (제목은 팝업 헤더가 대신하므로 생략)
 */
const AvailabilityCalendar = ({ variant = 'page' }) => {
    const [cursor, setCursor]   = useState(THIS_MONTH);
    const [booked, setBooked]   = useState({});
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);

    const cursorRef = useRef(cursor);
    cursorRef.current = cursor;

    const load = useCallback(async ({ year, month }, { silent = false, signal } = {}) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetchAvailability(year, month, signal);
            // 응답이 도착하는 사이 사용자가 달을 넘겼으면 버립니다.
            if (cursorRef.current.year !== year || cursorRef.current.month !== month) return;
            setBooked(res.booked || {});
            setError(null);
            setUpdatedAt(new Date());
        } catch (err) {
            if (err.name === 'AbortError') return;
            if (!silent) setError('예약현황을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // 달 이동 시 조회
    useEffect(() => {
        const controller = new AbortController();
        setSelected(null);
        load(cursor, { signal: controller.signal });
        return () => controller.abort();
    }, [cursor, load]);

    // 실시간 갱신 — 탭이 보일 때만 폴링합니다.
    useEffect(() => {
        const tick = () => {
            if (document.visibilityState === 'visible') load(cursorRef.current, { silent: true });
        };
        const timer = setInterval(tick, REFRESH_MS);
        document.addEventListener('visibilitychange', tick);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [load]);

    const monthOffset =
        (cursor.year - THIS_MONTH.year) * 12 + (cursor.month - THIS_MONTH.month);
    const canGoPrev = monthOffset > 0;
    const canGoNext = monthOffset < MAX_MONTHS_AHEAD;

    const move = (delta) => setCursor(prev => shiftMonth(prev.year, prev.month, delta));

    const selectedBooked = selected ? (booked[selected] || []) : [];
    const isSelectedPast = selected && selected < todayYmd();

    const isModal = variant === 'modal';

    return (
        <section className={`availability${isModal ? ' availability--modal' : ''}`}>
            {!isModal && (
                <div className="section-header">
                    <span className="section-label">AVAILABILITY</span>
                    <h2 className="section-title">실시간 예약현황</h2>
                    <p className="section-desc">
                        객실별 빈 자리를 바로 확인하실 수 있습니다.<br/>
                        예약은 전화로만 가능하니 원하시는 날짜를 확인 후 연락 주세요.
                    </p>
                </div>
            )}

            <div className="avail-panel">
                <div className="avail-toolbar">
                    <button
                        type="button"
                        className="avail-nav"
                        onClick={() => move(-1)}
                        disabled={!canGoPrev}
                        aria-label="이전 달"
                    >‹</button>

                    <div className="avail-month">
                        <strong>{cursor.year}년 {cursor.month}월</strong>
                        {updatedAt && (
                            <span className="avail-updated">
                                <i className="avail-live-dot" />
                                {updatedAt.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} 기준
                            </span>
                        )}
                    </div>

                    <button
                        type="button"
                        className="avail-nav"
                        onClick={() => move(1)}
                        disabled={!canGoNext}
                        aria-label="다음 달"
                    >›</button>
                </div>

                <div className="avail-legend">
                    {ROOMS.map(({ key, standard, color }) => (
                        <span key={key} className="avail-legend-item">
                            <i className="cal-dot" style={{ borderColor: color }} />
                            {key}<em>{standard}</em>
                        </span>
                    ))}
                    <span className="avail-legend-sep" />
                    <span className="avail-legend-item"><i className="cal-dot cal-dot--booked" />예약완료</span>
                </div>

                <div className={`avail-grid-wrap${loading ? ' is-loading' : ''}`}>
                    {error ? (
                        <div className="avail-message avail-message--error">
                            {error}
                            <button type="button" className="avail-retry" onClick={() => load(cursor)}>다시 시도</button>
                        </div>
                    ) : (
                        <>
                            <CalendarGrid
                                year={cursor.year}
                                month={cursor.month}
                                bookedByDate={booked}
                                selectedDate={selected}
                                onDayClick={(ymd) => setSelected(prev => (prev === ymd ? null : ymd))}
                            />
                            {loading && <div className="avail-spinner" aria-label="불러오는 중" />}
                        </>
                    )}
                </div>

                {selected && !error && (
                    <div className="avail-detail">
                        <div className="avail-detail-head">
                            <strong>{formatKoreanDate(selected)}</strong>
                            <button type="button" className="avail-detail-close" onClick={() => setSelected(null)} aria-label="닫기">✕</button>
                        </div>
                        {isSelectedPast ? (
                            <p className="avail-detail-past">지난 날짜입니다.</p>
                        ) : (
                            <ul className="avail-detail-list">
                                {ROOMS.map(({ key, standard, color }) => {
                                    const taken = selectedBooked.includes(key);
                                    return (
                                        <li key={key} className={taken ? 'is-taken' : ''}>
                                            <i className="cal-dot" style={taken ? undefined : { borderColor: color }} />
                                            <span className="avail-detail-room">{key}</span>
                                            <span className="avail-detail-standard">{standard}</span>
                                            <span className={`avail-detail-badge${taken ? ' is-taken' : ''}`}>
                                                {taken ? '예약완료' : '예약가능'}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                )}

                <div className="avail-foot">
                    <p>※ 표시된 현황은 실시간으로 반영되며, 최종 확정은 전화 통화 후 입금 시 완료됩니다.</p>
                    <div className="avail-foot-actions">
                        <a href="tel:010-2180-7979" className="avail-call">📞 010-2180-7979</a>
                        <Link to="/reservation" className="avail-more">요금·예약안내 보기</Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AvailabilityCalendar;